import React from 'react';

/**
 * SOC2 component displaying implementation categories and responsibilities
 * @returns {JSX.Element}
 */
function SOC2() {
  return (
    <div className="max-w-none">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">SOC 2</h1>
      
      <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100 mb-6">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">SOC 2 Implementation Categories and Responsibilities</h2>
        
        <div className="bg-purple-50 rounded-lg p-6 mb-8 border-l-4 border-purple-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Preamble</h3>
          <p className="text-gray-700 mb-4">
            Kolega is responsible for developing and managing the code base and platform infrastructure. 
            As such, we will handle specific aspects of SOC 2 compliance related to our technical 
            domain and development practices. However, there will still be requirements for clients to 
            complete aspects of SOC 2 not related to these technical areas.
          </p>
          <p className="text-gray-700">
            This document outlines the clear division of SOC 2 implementation responsibilities 
            between Kolega and our clients.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
                <span className="text-lg font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Technical Controls & Infrastructure</h3>
            </div>
            <div className="ml-14">
              <p className="text-purple-700 font-medium mb-3">Our Responsibility</p>
              <p className="text-gray-600 mb-3">
                Technical safeguards that protect the system:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Encryption of data (in transit and at rest)</li>
                <li>Network security (firewalls, IDS/IPS, segmentation)</li>
                <li>Access control mechanisms and authentication systems</li>
                <li>Vulnerability management and patching</li>
                <li>System monitoring and logging</li>
                <li>Backup and recovery systems</li>
                <li>Database controls and protection</li>
                <li>Physical security controls for our infrastructure</li>
                <li>Endpoint security for our systems</li>
                <li>Cloud security configurations</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
                <span className="text-lg font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Software Development & Change Management</h3>
            </div>
            <div className="ml-14">
              <p className="text-purple-700 font-medium mb-3">Our Responsibility</p>
              <p className="text-gray-600 mb-3">
                Controls ensuring secure software delivery:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Secure SDLC implementation</li>
                <li>Code review processes</li>
                <li>Change management procedures</li>
                <li>Testing methodologies (security, functional, QA)</li>
                <li>Deployment controls</li>
                <li>Development environment security</li>
                <li>Configuration management</li>
                <li>Release management</li>
                <li>Version control</li>
                <li>API security controls</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="p-3 rounded-full bg-amber-50 text-amber-600 mr-4">
                <span className="text-lg font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Organizational Policies & Procedures</h3>
            </div>
            <div className="ml-14">
              <p className="text-amber-700 font-medium mb-3">Client Responsibility</p>
              <p className="text-gray-600 mb-3">
                Administrative controls guiding behavior:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Company-wide security policies</li>
                <li>Employee onboarding/offboarding procedures</li>
                <li>Data classification policies</li>
                <li>Acceptable use guidelines</li>
                <li>Personnel security measures</li>
                <li>Vendor management program (non-technical)</li>
                <li>Physical security of client facilities</li>
                <li>Document management and retention</li>
                <li>Human resources security</li>
                <li>Clean desk policies</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="p-3 rounded-full bg-amber-50 text-amber-600 mr-4">
                <span className="text-lg font-bold">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Governance & Risk Management</h3>
            </div>
            <div className="ml-14">
              <p className="text-amber-700 font-medium mb-3">Client Responsibility</p>
              <p className="text-gray-600 mb-3">
                Oversight mechanisms ensuring control effectiveness:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Risk assessment frameworks</li>
                <li>Control monitoring and testing</li>
                <li>Management oversight committees</li>
                <li>Internal audit functions</li>
                <li>Compliance oversight</li>
                <li>Third-party risk management program</li>
                <li>Business continuity planning (organizational aspects)</li>
                <li>Reporting structures and escalation paths</li>
                <li>Security awareness program</li>
                <li>Compliance with regulatory requirements</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-4 text-gray-800">5. Aspects Requiring Integration</h3>
          <p className="text-gray-700 mb-6">
            For the following areas, Kolega will provide comprehensive documentation and evidence for the technical aspects we own. 
            Clients will need to incorporate and reference this information into their own SOC 2 documentation:
          </p>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Security (Common Criteria)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-purple-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>System architecture documentation with security controls</li>
                  <li>Network security implementation details</li>
                  <li>Encryption standards and implementations</li>
                  <li>Technical vulnerability management procedures</li>
                  <li>Security incident detection capabilities</li>
                  <li>Access control models within the platform</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Overall information security policy that references Kolega's platform</li>
                  <li>Organization-wide risk assessment incorporating the platform</li>
                  <li>Security awareness training that includes platform usage</li>
                  <li>Security incident response procedures that incorporate Kolega's capabilities</li>
                  <li>HR security procedures for staff with platform access</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Availability</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-purple-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>System uptime commitments and monitoring</li>
                  <li>Technical redundancy measures within the platform</li>
                  <li>Capacity management processes and metrics</li>
                  <li>Performance monitoring and alerting mechanisms</li>
                  <li>Technical disaster recovery procedures for the platform</li>
                  <li>System resilience testing results</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Business continuity plans incorporating the platform</li>
                  <li>Recovery time objectives aligned with Kolega's capabilities</li>
                  <li>Availability requirements for business processes</li>
                  <li>Communication procedures during outages</li>
                  <li>Staff training on alternative procedures during downtime</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Processing Integrity</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-purple-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Input validation controls within the platform</li>
                  <li>Error handling and exception management</li>
                  <li>Transaction processing controls</li>
                  <li>Data processing monitoring</li>
                  <li>System reconciliation mechanisms</li>
                  <li>Process timing and sequencing controls</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Data input procedures for users</li>
                  <li>Quality assurance processes that verify platform outputs</li>
                  <li>Reconciliation procedures for data processed by the platform</li>
                  <li>Manual verification procedures for critical transactions</li>
                  <li>End-user training on proper data entry</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Confidentiality</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-purple-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Data classification implementation within the platform</li>
                  <li>Technical access controls for confidential data</li>
                  <li>Data segregation mechanisms</li>
                  <li>Secure data deletion methods</li>
                  <li>Encryption for confidential data</li>
                  <li>Data leakage prevention measures</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Confidentiality agreements with employees and third parties</li>
                  <li>Data classification policy that aligns with platform capabilities</li>
                  <li>Procedures for handling confidential data within the platform</li>
                  <li>Confidential data inventory that includes data in the platform</li>
                  <li>Training on handling confidential information</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Privacy (if applicable)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-purple-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Technical privacy controls within the platform</li>
                  <li>User consent management capabilities</li>
                  <li>Data minimization features</li>
                  <li>Data retention and deletion capabilities</li>
                  <li>Privacy-enhancing technical features</li>
                  <li>Cross-border data transfer controls</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Privacy notices and policies</li>
                  <li>Procedures for handling data subject requests</li>
                  <li>Privacy impact assessments that include the platform</li>
                  <li>Consent management procedures</li>
                  <li>Staff training on privacy requirements</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Incident Management</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-purple-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Security event monitoring and alerting for the platform</li>
                  <li>Technical incident response capabilities</li>
                  <li>Forensic analysis capabilities for platform incidents</li>
                  <li>Incident containment mechanisms</li>
                  <li>Root cause analysis for technical incidents</li>
                  <li>Security incident logs and documentation</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Organizational incident response plan that includes Kolega</li>
                  <li>Escalation procedures for incidents involving the platform</li>
                  <li>Communication templates for stakeholder notification</li>
                  <li>Legal and regulatory breach notification processes</li>
                  <li>Post-incident review procedures</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Change Management</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-purple-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Platform change control procedures</li>
                  <li>Testing procedures for platform changes</li>
                  <li>Release notes and documentation</li>
                  <li>Change impact assessment for platform updates</li>
                  <li>Rollback capabilities for platform changes</li>
                  <li>Emergency change procedures</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Change advisory board that evaluates platform changes</li>
                  <li>User communication procedures for platform changes</li>
                  <li>User acceptance testing procedures</li>
                  <li>Business impact assessments for platform changes</li>
                  <li>Training plans for significant platform changes</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Vendor Management</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-purple-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Information about our technical subservice organizations</li>
                  <li>Our security assessments of key technical vendors</li>
                  <li>Technical compliance information about our service providers</li>
                  <li>Monitoring results for critical technical vendors</li>
                  <li>Contract requirements with our technical vendors</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Vendor management program that includes Kolega</li>
                  <li>Risk assessment of Kolega as a vendor</li>
                  <li>Contractual requirements documentation</li>
                  <li>Vendor performance reviews</li>
                  <li>Vendor monitoring program</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SOC2;
